import React from 'react';
import { BoxProps, Box, Flex, HStack, Icon, Button } from '@chakra-ui/react';
import { ChevronRightIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import tinycolor from 'tinycolor2';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { CustomText } from '../atoms';
import { getIconFromString } from './MenuCard';

export type SectionCardProps = BoxProps & {
  sectionName: string;
  icon: string;
  bgColor?: string;
  textColor?: string;
  faqs: {
    id?: string;
    publish_id: string;
    faq_title: string;
  }[];
  onClickMore: (formattedSectionTitle: string) => void;
  onClickFaq: (formattedFaqTitle: string) => void;
  sectionHelperText: string;
  helperTextColor?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  categoriesHeadingTextColor?: string;
};

export const SectionCardPreview: React.FC<SectionCardProps> = ({
  sectionName,
  icon,
  bgColor,
  textColor,
  faqs,
  onClickMore,
  onClickFaq,
  sectionHelperText,
  helperTextColor,
  primaryAccentColor,
  secondaryAccentColor,
  categoriesHeadingTextColor,
}) => {
  const lightenedBorderColor = tinycolor(textColor).lighten(90).toString();

  return (
    <Box
      borderWidth="1px"
      overflow="hidden"
      backgroundColor={bgColor}
      color={textColor}
      position="relative" // Ensures date can be positioned in bottom-right
      height="100%" // Ensure box takes full height for positioning
      boxShadow="md"
    >
      {/* Flexbox to align the title at the top */}
      <Flex direction="column" justify="space-between">
        <HStack
          cursor="pointer"
          align="center"
          borderBottom={`1px solid ${lightenedBorderColor}`}
          p={3}
          bgColor={primaryAccentColor}
          minH="5rem"
        >
          <Icon
            as={getIconFromString(icon) || FaRegQuestionCircle}
            boxSize="8" // Increase the size (e.g., 8 is larger than the default size)
            color={categoriesHeadingTextColor} // Set a vibrant color (replace with your preferred color)
          />

          <Box
            as="span"
            cursor="pointer"
            onClick={() => onClickMore(sectionName.toLowerCase().replace(/\s+/g, '-'))}
          >
            <CustomText fontSize="lg" fontWeight="bold" color={categoriesHeadingTextColor}>
              {sectionName}
            </CustomText>
          </Box>
        </HStack>
        {/* Map through faqs and slice based on a static limit of 3 */}
        {faqs?.slice(0, 3).map(faq => {
          return (
            <HStack
              justify="space-between"
              align="center"
              borderBottom={`1px solid ${lightenedBorderColor}`}
              p={4}
              _hover={{ boxShadow: 'md', transform: 'scale(1.05)' }}
              key={faq.publish_id}
              onClick={() => onClickFaq(faq.publish_id)}
            >
              <CustomText color={textColor} fontSize="sm" fontWeight="medium">
                {faq.faq_title}
              </CustomText>
              <ChevronRightIcon color={textColor} />
            </HStack>
          );
        })}

        {/* Show "More" button if there are more than 3 faqs */}
        {faqs?.length > 3 && (
          <HStack justify="flex-end" p={4} mt="auto">
            <Button
              color={helperTextColor}
              onClick={() => onClickMore(sectionName.toLowerCase().replace(/\s+/g, '-'))}
              size="sm"
              variant="link"
              colorScheme="teal"
            >
              {sectionHelperText}
            </Button>
            <ExternalLinkIcon color={secondaryAccentColor} />
          </HStack>
        )}
      </Flex>
    </Box>
  );
};
